import { createLazyResource, createMagicDoorContext, useLocalization } from '@magicdoorinc/ui';
import { useNavigate, useParams, useSearchParams } from '@solidjs/router';
import { createEffect, createMemo } from 'solid-js';
import {
  DEPOSIT_TYPE_DICT,
  TRANSACTION_TYPE_DICT,
} from '~/pages/leases/lease-details/components/lease-deposit-transaction/transactionConfigs';
import type {
  LeaseChargeCreditDto,
  LeaseChargeDto,
  LeaseChargeLedgerEntryType,
  LeaseDepositDto,
  LeaseDepositLedgerEntryType,
  LeaseLateFeeDto,
  TransactionDto,
} from '~/swagger/portal/Api';

export type LeaseChargeOrDeposit = LeaseChargeDto | LeaseChargeCreditDto | LeaseLateFeeDto | LeaseDepositDto | TransactionDto;

type LeaseDepositDetailModalProps = {
  defaultCategory: Category;
  fetchEntity: () => Promise<LeaseChargeOrDeposit | undefined>;
};

export enum Category {
  Charge = 'charge',
  Deposit = 'deposit',
}

export const [ChargeOrDepositDetailProvider, useChargeOrDepositDetail] = createMagicDoorContext(
  'ChargeOrDepositDetail',
  (props: LeaseDepositDetailModalProps) => {
    const { t } = useLocalization();
    const navigate = useNavigate();
    const params = useParams<{ leaseId: string; transactionId: string }>();
    const [searchParams] = useSearchParams<{ type: LeaseChargeLedgerEntryType | LeaseDepositLedgerEntryType }>();

    const [entity, entityActions] = createLazyResource(async () => {
      return await props.fetchEntity();
    });

    const modalTitle = createMemo(() => {
      if (entity.loading || !entity()) return t('Loading');

      if (props.defaultCategory === Category.Charge) {
        return `${t(TRANSACTION_TYPE_DICT.getLabelByValue(searchParams.type as LeaseChargeLedgerEntryType))} ${t('details')}`;
      } else if (props.defaultCategory === Category.Deposit) {
        return `${t(DEPOSIT_TYPE_DICT.getLabelByValue(searchParams.type as LeaseDepositLedgerEntryType))} ${t('details')}`;
      }

      return t('Details');
    });

    const handleCancel = () => {
      if (props.defaultCategory === Category.Charge) {
        navigate(`/leasing/leases/${params.leaseId}/transactions`, { scroll: false, replace: true });
      } else if (props.defaultCategory === Category.Deposit) {
        navigate(`/leasing/leases/${params.leaseId}/deposits`, { scroll: false, replace: true });
      }
    };

    createEffect(() => {
      params.transactionId;
      entityActions.refetch();
    });

    return {
      category: props.defaultCategory,
      get type() {
        return searchParams.type;
      },
      modalTitle,
      handleCancel,
      get entity() {
        return entity;
      },
      entityActions,
    };
  }
);
